import { useEffect, useState } from "react";
import { DEFAULT_UNITS_PREFERENCE, type UnitsPreference } from "@heraion/shared";

import { useMaxEditor, type UseMaxEditorDeps } from "./useMaxEditor";
import { EditMaxSheet } from "./EditMaxSheet";
import type { UiTheme } from "../theme";

// PG-11 — the MAX editor, openable from somewhere that is not the MAXes
// screen. Antenore, 2026-09-02: «io metterei la pastiglietta che apre il
// configuratore del MAX per quell'esercizio, nel widget dello storico...
// utilizzando lo stesso widget di inserimento che si usa in Me -> MAXes.
// Facendo così il Max è configurabile in 3 posti diversi».
//
// ⛔ Shared, not re-implemented per host. `useMaxEditor` already owns the
// PUT/DELETE orchestration and `EditMaxSheet` the chrome; what was missing was
// only a way to open them without `MaxesView`, which is the whole list. A
// second editor would have been a second mutation path onto the same 1RM.
//
// ⛔ And it hands back a NODE plus a callback rather than taking over the
// screen: the sheet is a full-screen Modal, so it has to render at the host's
// top level, not inside the history bottom-sheet whose button raised it.
//
// The host keeps `writeMax`, and that is deliberate: mobile's routes through
// `maxOutbox` and web's straight through, and the difference is the whole
// reason `MaxWriteFn` is a seam (see useMaxEditor's header — two queues on one
// MAX would resolve in whichever order they happened to drain).
export function useMaxEditorHost(
  deps: Omit<UseMaxEditorDeps, "units"> & {
    /** null = the athlete's unit is not known yet (web reads the profile
     *  after mounting). A tap then waits for it: the sheet never opens in a
     *  guessed unit (MX-6). */
    units: UnitsPreference | null;
    theme: UiTheme;
    /** Display name for the sheet title, resolved by the host's catalog. */
    exerciseName: (slug: string) => string;
    /** Safe-area insets; mobile feeds useSafeAreaInsets, web passes none. */
    sheetInsets?: { top: number; bottom: number };
  },
): {
  /** Open the editor for one exercise — this is what `LoggerDeps.onEditMax`
   *  wants. */
  onEditMax: (exerciseId: string) => void;
  /** Render this at the host's top level. */
  sheet: React.ReactElement;
} {
  const { theme, exerciseName, sheetInsets, units, ...editorDeps } = deps;
  // The default never seeds a sheet: openEdit only runs once `units` is known.
  const editor = useMaxEditor({
    ...editorDeps,
    units: units ?? DEFAULT_UNITS_PREFERENCE,
  });
  const [pendingSlug, setPendingSlug] = useState<string | null>(null);
  const { openEdit } = editor;
  useEffect(() => {
    if (units === null || pendingSlug === null) return;
    openEdit(pendingSlug);
    setPendingSlug(null);
  }, [units, pendingSlug, openEdit]);

  return {
    onEditMax: (exerciseId: string) => {
      if (units === null) setPendingSlug(exerciseId);
      else openEdit(exerciseId);
    },
    sheet: (
      <EditMaxSheet
        theme={theme}
        visible={editor.editingSlug !== null}
        exerciseName={
          editor.editingSlug ? exerciseName(editor.editingSlug) : ""
        }
        value={editor.editValue}
        onChangeValue={editor.setEditValue}
        unit={editor.editUnit}
        onToggleUnit={editor.toggleUnit}
        busy={editor.editBusy}
        error={editor.editError}
        canClear={editor.canClear}
        onCancel={editor.closeEdit}
        onSave={() => void editor.save()}
        onClear={() => void editor.clear()}
        topInset={sheetInsets?.top}
        bottomInset={sheetInsets?.bottom}
      />
    ),
  };
}
